import lawImg from "@/assets/industry-law.jpg";
import medImg from "@/assets/industry-medical.jpg";
import reImg from "@/assets/industry-realestate.jpg";
import fitImg from "@/assets/industry-fitness.jpg";

export type FaqItem = { q: string; a: string };

export type Industry = {
  slug: "law-firms" | "medical-dental" | "real-estate" | "fitness";
  title: string; // Short display name
  benefit: string; // one-line benefit
  bullets: [string, string]; // exactly 2 bullets for the card
  roi: string; // short ROI line
  img: string;
  alt: string;
  automate: string[]; // What we automate (3–6)
  quickWins: string[]; // Quick wins for the detail page
  steps: string[]; // How it works steps (3–5)
  faq: FaqItem[]; // FAQ entries (0–8)
  cta: string; // CTA copy
};

export const industries: Industry[] = [
  {
    slug: "law-firms",
    title: "Law Firms",
    benefit: "Speed up contract review and client intake with AI.",
    bullets: [
      "Automate contract summarization & precedent search",
      "24/7 AI receptionist to qualify and schedule",
    ],
    roi: "ROI: +5 hrs/week; $1k+/mo recaptured",
    img: lawImg,
    alt: "Law books on a shelf",
    automate: [
      "Contract summarization and clause extraction",
      "Precedent and case search assistants",
      "AI receptionist for intake and scheduling",
    ],
    quickWins: [
      "Cut first-draft review time by 50%",
      "Auto-route and prioritize client inquiries",
    ],
    steps: [
      "Discovery of workflows and tools",
      "Pilot an AI assistant on a narrow task",
      "Expand automation and measure ROI",
    ],
    faq: [
      { q: "Is my data confidential?", a: "Yes. We use secure, access-controlled environments and can integrate with your DMS." },
      { q: "Do you support small boutique firms?", a: "Absolutely. We tailor scope to solo and small teams with quick wins first." },
    ],
    cta: "Book a free consultation for law firms",
  },
  {
    slug: "medical-dental",
    title: "Medical/Dental",
    benefit: "Reduce no-shows with smart reminders and easy intake.",
    bullets: ["HIPAA-safe reminders & eligibility checks", "Automated intake and follow-ups"],
    roi: "ROI: one missed appointment saved each week",
    img: medImg,
    alt: "Bright medical clinic hallway",
    automate: [
      "Patient intake chat and forms",
      "Insurance eligibility checks",
      "No-show reduction reminders",
    ],
    quickWins: [
      "Instant appointment confirmations",
      "Automated pre-visit instructions",
    ],
    steps: [
      "Review of EHR and scheduling tools",
      "Enable reminder workflows",
      "Add intake and eligibility automation",
    ],
    faq: [
      { q: "HIPAA compliance?", a: "We follow HIPAA best practices and can work with BAAs where required." },
      { q: "Will it replace staff?", a: "No—automation assists staff by removing repetitive tasks." },
    ],
    cta: "Book a free consultation for your practice",
  },
  {
    slug: "real-estate",
    title: "Real Estate",
    benefit: "Qualify leads and follow up automatically.",
    bullets: [
      "AI lead-qualifying chat & auto follow-up",
      "Listing generator and next-step reminders",
    ],
    roi: "ROI: more closings with fewer empty slots",
    img: reImg,
    alt: "Suburban house exterior",
    automate: [
      "Lead capture and qualification",
      "Automated follow-ups and reminders",
      "Listing description generator",
    ],
    quickWins: ["Faster lead response times", "Fewer missed showings"],
    steps: [
      "Connect CRM and channels",
      "Deploy lead-qualifying assistant",
      "Automate follow-ups",
    ],
    faq: [
      { q: "Do you integrate with my CRM?", a: "We support popular CRMs and can add custom connectors." },
    ],
    cta: "Book a free consultation for real estate",
  },
  {
    slug: "fitness",
    title: "Fitness",
    benefit: "Fill classes and retain members with AI.",
    bullets: ["Predict class fill & auto booking", "Member Q&A assistant and retention"],
    roi: "ROI: +$100s/mo per retained member",
    img: fitImg,
    alt: "Modern fitness studio interior",
    automate: [
      "Waitlist and auto-book flows",
      "Member Q&A assistant",
      "Churn risk detection",
    ],
    quickWins: ["Higher class utilization", "Reduced churn"],
    steps: ["Connect booking software", "Enable waitlist automation", "Add retention assistant"],
    faq: [
      { q: "Which platforms do you support?", a: "We integrate with common booking platforms; ask us about yours." },
    ],
    cta: "Book a free consultation for fitness",
  },
];

export function getIndustryBySlug(slug: Industry["slug"]) {
  return industries.find((i) => i.slug === slug);
}
